import { Injectable } from '@angular/core';
import { FirebaseApp } from 'angularfire2';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/toPromise';
import { AngularFireDatabase } from 'angularfire2/database';
import 'firebase/storage';
import * as firebase from 'firebase/app';
import { EXIFStatic } from 'exif-js';

declare const EXIF: EXIFStatic;

@Injectable()
export class StorageService {
  /** Storage reference */
  private storageRef;

  constructor(private app: FirebaseApp, private db: AngularFireDatabase) {
    this.storageRef = this.app.storage().ref();
  }

  /**
   * Uploads a file to the storage bucket
   * @param file
   * @param name
   * @param path
   */
  public upload(file: Blob, name: string, path: string): { progress: Subject<number>, url: Subject<string> } {

    /** Progress of the upload stream*/
    const progress$ = new Subject<number>();

    const url$ = new Subject<string>();

    const reportGalleryRef = this.storageRef.child(path);

    console.log(reportGalleryRef);

    const uploadTask = reportGalleryRef.put(file, { name });

    uploadTask.on('state_changed', (snapshot) => {
      /** Progress of the upload */
      let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

      console.log(`Upload is ${progress} done`);

      progress$.next(progress);
    }
      , error => {
        console.error('Upload to storage bucket fail', error);

        url$.error('Upload to storage bucket fail');

      }
      , () => {
        const downloadURL = uploadTask.snapshot.downloadURL;
        console.debug('upload success', downloadURL);

        url$.next(downloadURL);

        // Completing all subjects to avoid memory leaks
        //
        [url$, progress$].forEach(subject => subject.complete());

    });

    return { progress: progress$, url: url$ };
  }

  /** Gets image meta data for a given path */
  public getImage( path: string ): void {
    const ref = this.storageRef.child(path);

    ref.getMetadata().then(metaData => {
      console.log(metaData);
    }).catch(err => console.error(err));

  }

  /**
   * Async transforms dataURI to a blob
   * @author devnull69
   * @see https://stackoverflow.com/a/12300351/4047409
   * @param dataURI
   */
  public dataURLToBlob(dataURI: string, callback: (blob: Blob) => void ): void {

    const perfStart = performance.now();

    // convert base64 to raw binary data held in a string
    //
    let byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    //
    let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

    // write the bytes of the string to an ArrayBuffer
    //
    let ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    //
    let ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    byteString.split('').forEach((el, i) => {
      ia[i] = byteString.charCodeAt(i);
    });

    // write the ArrayBuffer to a blob, and you're done
    let blob = new Blob([ab], {type: mimeString});

    // Callback for the blob
    //
    callback(blob);

    const perfEnd = performance.now();

    console.log(`Created a blob in  ${ perfEnd - perfStart  }ms`);
  }

  /**
   * Saves the image in the database in two places ( images/:imageid and gallery/:reportid/:category/:imageid )
   * @param imageData
   * @param imageId
   * @param originalImageId incase the image is being replaced, we want to save the reference to the original image
   */
  public saveImageInDatabase(imageData: northseafood.IDbImage, imageId: string, originalImage: northseafood.IOriginalImage = null): firebase.Promise<void> {

    const { authorId, storageBucketPath, reportId, url, category } = imageData;

    /** Object containing multiple places where we are going to write our data */
    const writeOperation = {};

    /**
     * Represents images under images/:imaegId
     */
    const databaseImage: northseafood.IDbImage = {
      reportId: reportId,
      storageBucketPath,
      url,
      category: category,
      authorId
    };

    /**
     * Represents images under gallery/:reportId/:category/:imageId
     */
    const databaseGalleryImage: northseafood.IDbGalleryImage = {
      url,
      storageBucketPath,
      authorId,
      // This could be a null value, but thats fine because firebase takes out null values and doens't save them in
      // the database
      //
      originalImage
    }

    writeOperation[`images/${imageId}`] = databaseImage;

    writeOperation[`gallery/${reportId}/${category}/${imageId}`] = databaseGalleryImage;

    return this.db.object('/').update(writeOperation);
  }

  /**
   * Calculates how many degrees an image requires rotation by reading its exif data
   * this will ensure the image has the correct oreintation
   * @param image
   */
  public calculateRotation(image: File | HTMLImageElement): number {
    const exif = EXIF.readFromBinaryFile(image);

    /** How many degrees to rotate */
    let rotationVal = 0;

    switch(exif.Orientation){
        case 8:
            rotationVal = 90*Math.PI/180;
            console.log('Calculating rotation: ', 8);
            break;
        case 3:
            rotationVal = 180*Math.PI/180;
            console.log('Calculating rotation: ', 3);
            break;
        case 6:
            rotationVal = -90*Math.PI/180;
            console.log('Calculating rotation: ', 6);
        break;
    }

    return rotationVal;
  }

  public deleteImage(imageData: northseafood.IDbImage, imageId: string): firebase.Promise<void>  {

    const imageRef = this.storageRef.child(`reports/${imageData.reportId}/images/${imageId}`);

    /** Object containing multiple places where we are going to delete the data */
    const deleteOperation = {};

    deleteOperation[`gallery/${imageData.reportId}/${imageData.category}/${imageId}`] = null
    deleteOperation[`images/${imageId}`] = null;

    // First we will delete the item from the database, then from the storage bucket
    //

    const promise = this.db.object(`/`)
      .update(deleteOperation)
      .then(() => {
        return imageRef.delete();
      });

    return promise;
  }
}



// WEBPACK FOOTER //
// ./src/providers/storage.service.ts